import { Text } from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import { canAccessWebNavigationModule } from "../../../lib/access";
import {
  type NavigationOrigin,
  toOriginSearch,
} from "../../../lib/navigationOrigin";
import { stripOriginSearch } from "../../../lib/navigationOriginKeys";
import type { AssetId, WorkOrderId } from "../../../lib/workIds";
import { getWorkScopeSearch } from "../../../lib/workSearch";
import { useWorkScope } from "../../WorkScopeProvider";

/**
 * A Work Order's asset, linked to the Asset page with the Work Order as the
 * origin (so the Asset page shows "Back to WO-..."). Users without Assets
 * access, or without a known facility scope, get plain text.
 */
export function WorkOrderAssetLink({
  assetId,
  label,
  originSearch,
  parentOrigin,
  workOrderId,
}: {
  assetId: AssetId;
  label: string;
  /** The Work Orders page's current search, reduced to list state for the back link. */
  originSearch?: object;
  /** The record this Work Order was itself opened from, kept for the Back chain. */
  parentOrigin?: NavigationOrigin;
  workOrderId: WorkOrderId;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const canLinkAsset = Boolean(
    selectedCustomer &&
      selectedFacility &&
      canAccessWebNavigationModule(selectedFacility, "ASSETS"),
  );
  if (!canLinkAsset || !selectedCustomer || !selectedFacility) {
    return <Text as="span">{label}</Text>;
  }

  return (
    <Text as="span">
      <Link
        className="box-decoration-clone rounded-(--radius-xs) text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-button-focus-ring wrap-anywhere"
        params={{ assetId }}
        search={{
          ...getWorkScopeSearch(
            {
              id: selectedCustomer.id,
              facilities: { length: selectedCustomer.facilities.length },
            },
            { id: selectedFacility.id },
          ),
          ...toOriginSearch(
            {
              type: "workOrder",
              id: workOrderId,
              search: originSearch
                ? stripOriginSearch(originSearch)
                : undefined,
              parent: parentOrigin,
            },
            { type: "asset", id: assetId },
          ),
        }}
        to="/assets/$assetId"
      >
        {label}
      </Link>
    </Text>
  );
}
